import { PAGINATION_LIMIT } from '@constants/generic';
import { MOCK_FORMS_LIST } from '@features/forms/constants';
import {
  MOCK_FORM_DROP_OFFS,
  MOCK_FORM_SUBMISSIONS
} from '@features/forms/constants/mockApiData';
import { MOCK_FORM_ACTIVITY_LOG_API } from '@features/forms/constants/mockFormActivityLogApi';
import { getMockFormOverviewApi } from '@features/forms/constants/mockFormOverviewApi';
import {
  buildMockUpdateFormSourcesResponse,
  getMockFormShareLinksForForm
} from '@features/forms/constants/mockFormSourceApi';
import { MOCK_FORM_TEMPLATES_API } from '@features/forms/constants/mockFormTemplatesApi';
import { getMockSubmissionDetailApi } from '@features/forms/constants/mockSubmissionDetailApi';
import { filterMockActivityLog } from '@features/forms/helpers/filterMockActivityLog';
import { filterMockDropOffs } from '@features/forms/helpers/filterMockDropOffs';
import { filterMockFormsList } from '@features/forms/helpers/filterMockFormsList';
import { filterMockSubmissions } from '@features/forms/helpers/filterMockSubmissions';
import { parseFormsListQueryFromUrl } from '@features/forms/helpers/parseFormsListQueryFromUrl';
import {
  DropOffStatusFilter,
  FormStatus,
  FormSubmission
} from '@features/forms/types';
import { UpdateFormSourcesApiRequest } from '@features/forms/types/formSourceApi';
import { FormSubmissionApiItem } from '@features/forms/types/formSubmissionApi';

// Live, mutable copy so demo actions (pause/resume/close a form) persist for
// the rest of the session. Resets on page reload.
let trialFormsList = [...MOCK_FORMS_LIST];

const toSubmissionApiItem = (row: FormSubmission): FormSubmissionApiItem => ({
  id: row.id,
  name: row.name,
  mobile: row.phone,
  add_to_mode: row.addedTo.mode,
  group: row.addedTo.groupId
    ? { id: row.addedTo.groupId, name: row.addedTo.groupName ?? '' }
    : null,
  source: row.source,
  paid_at: row.paidAt,
  version: row.formVersion,
  total_amount: row.totalAmount,
  paid_amount: row.amountPaid,
  is_partial_payment_enabled: false,
  payment_order_id: row.paymentId,
  member_id: row.memberId,
  settlement_status:
    row.paymentStatus === 'processing' ? 'processing' : 'settled'
});

export const getFormsListMock = (url: string) => {
  const params = parseFormsListQueryFromUrl(new URL(url, 'http://mock'));
  return filterMockFormsList(trialFormsList, params);
};

export const getFormStatusCountsMock = () => ({
  published: trialFormsList.filter((item) => item.status === 'published')
    .length,
  paused: trialFormsList.filter((item) => item.status === 'paused').length,
  closed: trialFormsList.filter((item) => item.status === 'closed').length
});

export const getFormOverviewMock = (formId: string) =>
  getMockFormOverviewApi(formId);

export const createFormMock = () => ({
  id: 'form_new_mock',
  name: 'New form',
  status: 'published',
  description: '',
  image_url: '',
  form_link: 'https://forms.cofee.life/form_new_mock',
  version: 1,
  is_source_tracking_enabled: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
});

export const updateFormMock = (formId: string) => ({
  id: formId,
  name: 'Updated form',
  status: 'published',
  description: '',
  image_url: '',
  form_link: `https://forms.cofee.life/${formId}`,
  version: 3,
  is_source_tracking_enabled: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
});

export const updateFormStatusMock = (formId: string, status: string) => {
  trialFormsList = trialFormsList.map((item) =>
    item.id === formId ? { ...item, status: status as FormStatus } : item
  );
  const form =
    trialFormsList.find((item) => item.id === formId) ?? trialFormsList[0];

  return {
    id: formId,
    name: form.name,
    status,
    description: 'Mock form description',
    image_url: '',
    form_link: `https://forms.cofee.life/${formId}`,
    version: 2,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

export const getFormSubmissionsMock = (params: URLSearchParams) => {
  const page = Number(params.get('page') ?? '0');
  const limit = Number(params.get('limit') ?? PAGINATION_LIMIT);
  const search = params.get('search') ?? undefined;
  const { data, pagination } = filterMockSubmissions(MOCK_FORM_SUBMISSIONS, {
    page,
    limit,
    search
  });

  return {
    data: data.map(toSubmissionApiItem),
    pagination: {
      page: pagination.page,
      limit: pagination.limit,
      total_rows: pagination.totalRows,
      last_page: pagination.lastPage
    }
  };
};

export const getFormSubmissionDetailMock = (submissionId: string) =>
  getMockSubmissionDetailApi(submissionId) ?? null;

export const exportFormSubmissionsMock = () => ({
  job_id: 'mock-form-submissions-export'
});

export const getFormDropOffsMock = (params: URLSearchParams) => {
  const page = Number(params.get('page') ?? '0');
  const limit = Number(params.get('limit') ?? PAGINATION_LIMIT);
  const search = params.get('search') ?? undefined;
  const status = (params.get('status') ?? 'all') as DropOffStatusFilter;
  const { data, pagination } = filterMockDropOffs(MOCK_FORM_DROP_OFFS, {
    page,
    limit,
    search,
    status
  });

  return {
    data: data.map((row) => ({
      id: row.id,
      name: row.name,
      mobile: row.phone,
      status: row.status,
      source: row.source,
      submitted_at: row.submittedAt,
      total_amount: row.totalAmount,
      payment_order_id: row.paymentOrderId,
      reminder_message: row.reminderMessage
    })),
    pagination: {
      page: pagination.page,
      limit: pagination.limit,
      total_rows: pagination.totalRows,
      last_page: pagination.lastPage
    }
  };
};

export const exportFormDropOffsMock = () => ({
  job_id: 'mock-form-drop-offs-export'
});

export const getFormActivityLogMock = (params: URLSearchParams) => {
  const page = Number(params.get('page') ?? '0');
  const limit = Number(params.get('limit') ?? PAGINATION_LIMIT);
  return filterMockActivityLog(MOCK_FORM_ACTIVITY_LOG_API, { page, limit });
};

export const getFormShareLinksMock = (formId: string) =>
  getMockFormShareLinksForForm(formId);

export const updateFormSourcesMock = (
  formId: string,
  body: UpdateFormSourcesApiRequest
) => buildMockUpdateFormSourcesResponse(formId, body);

export const getFormTemplatesMock = () => MOCK_FORM_TEMPLATES_API;

export const getFormPresignedUrlMock = () => ({
  file_id: `mock-file-${Date.now()}`,
  access_key: 'mock-access-key',
  presigned_url: 'https://mock-uploads.cofee.life/forms/cover-image'
});
